import {
  StagedTxn, BalancePoint, TxnSource, ImportRecord, COMMIT_CHUNK_SIZE,
} from '../money.model';

export interface CommitInput {
  importId: string;
  filename: string;
  source: TxnSource;
  accountId: string;
  txns: StagedTxn[];
  balances: BalancePoint[];
  rowCount: number;
  duplicateCount: number;
  pendingCount: number;
  now: number;
}

export interface CommitChunk {
  payload: Record<string, unknown>;
  txnIds: string[];
}

/**
 * Splits an import into RTDB multi-path updates of at most COMMIT_CHUNK_SIZE
 * transactions each. The real bank export is 1,510 records — ~3,000 paths in one
 * write is enough to fail or time out.
 *
 * The import record goes in the LAST chunk on purpose: if the run is interrupted,
 * there is no receipt claiming an import that only partially happened, and the
 * orphaned transactions are recognized by the next import's dedupe pass.
 */
export function buildCommitChunks(input: CommitInput): CommitChunk[] {
  const fresh = input.txns.filter(t => !t.isDuplicate);
  const chunks: CommitChunk[] = [];

  for (let i = 0; i < fresh.length; i += COMMIT_CHUNK_SIZE) {
    const slice = fresh.slice(i, i + COMMIT_CHUNK_SIZE);
    const payload: Record<string, unknown> = {};
    const txnIds: string[] = [];

    slice.forEach((t, j) => {
      const txnId = `${input.importId}-${i + j}`;
      txnIds.push(txnId);
      payload[`transactions/${txnId}`] = {
        date: t.date,
        amount: t.amount,
        description: t.description,
        merchant: t.merchant,
        matchKey: t.matchKey,
        accountId: t.accountId,
        categoryId: t.categoryId ?? null,
        isTransfer: t.isTransfer,
        excluded: false,
        source: t.source,
        importId: input.importId,
        fingerprint: t.fingerprint,
        note: '',
        purchasedBy: t.purchasedBy,
        sourceCategory: t.sourceCategory,
      };
      payload[`fingerprints/${t.fingerprint}`] = txnId;
    });

    chunks.push({ payload, txnIds });
  }

  // Balances ride along in the first chunk; an all-duplicate import still needs one chunk.
  if (chunks.length === 0) chunks.push({ payload: {}, txnIds: [] });
  input.balances.forEach(b => {
    chunks[0].payload[`balances/${b.accountId}/${b.date}`] = b.balance;
  });

  const allIds: string[] = [];
  chunks.forEach(c => allIds.push(...c.txnIds));
  const dates = fresh.map(t => t.date).sort();

  const record: ImportRecord = {
    id: input.importId,
    filename: input.filename,
    source: input.source,
    accountId: input.accountId,
    rowCount: input.rowCount,
    importedCount: allIds.length,
    duplicateCount: input.duplicateCount,
    pendingCount: input.pendingCount,
    dateFrom: dates[0] ?? '',
    dateTo: dates[dates.length - 1] ?? '',
    txnIds: allIds,
    createdAt: input.now,
  };

  chunks[chunks.length - 1].payload[`imports/${input.importId}`] = record;
  return chunks;
}
